import { get } from "lodash";
import React from "react";
import { useWatch, ArrayField } from "react-hook-form";
import { TemplateQuestionVariables } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";

import { FormStepProps } from "../../screens/MadFormScreen";
import { ClockIcon, ChatIcon, IconProps, StarIcon } from "../icons";
import { OList, Tooltip } from "../wombat-ui";
import { TemplateQuestionFormInput } from "./TemplateQuestionForm";

type TemplateQuestionPreviewProps = Pick<
  FormStepProps,
  "control" | "errors"
> & {
  name: string;
  icon: React.ReactElement<IconProps>;
  focused: boolean;
  defaultValue: Partial<ArrayField<TemplateQuestionFormInput, "id">>;
};

const TemplateQuestionPreview: React.FC<TemplateQuestionPreviewProps> = ({
  control,
  errors,
  name,
  focused,
  icon,
  defaultValue,
}) => {
  // FIXME: not sure about the typing here
  const state: Omit<TemplateQuestionVariables, "id"> = useWatch({
    control,
    name,
    defaultValue,
  });
  const t = useTranslations();

  return (
    <div
      id={`${name}.previewContainer`}
      className={`${
        focused ? "hidden" : ""
      } px-4 py-3 bg-white rounded shadow-md ${
        get(errors, name) ? "ring-2 ring-error-400" : ""
      }`}
    >
      <div className="grid w-full grid-cols-12 gap-x-4 gap-y-2">
        <div className="flex items-center w-full col-span-10 space-x-6">
          <span className="flex items-center justify-center">{icon}</span>
          <span className="text-sm">
            {state?.label || t("templates.questionPreviews.unlabeledQuestion")}
          </span>
        </div>
        <div className="flex justify-end col-span-2 space-x-2">
          {state?.referentIds?.length > 0 && (
            <Tooltip
              label={t("templates.questionPreviews.referentOpinionAsked")}
            >
              <ChatIcon iconClassName="w-5 h-5 text-light-900" />
            </Tooltip>
          )}
          {state?.timeTracked && (
            <Tooltip label={t("templates.questionPreviews.timeSpentAsked")}>
              <ClockIcon iconClassName="w-5 h-5 text-light-900" />
            </Tooltip>
          )}
          {(state?.type === "TemplateQuestionSkill" ||
            state?.type === "TemplateQuestionEvaluation") &&
            state?.options?.find(({ isGoal }) => isGoal) && (
              <Tooltip
                label={t("templates.questionPreviews.goalLevelDefined", {
                  level: state?.options?.find(({ isGoal }) => isGoal)?.label,
                })}
              >
                <StarIcon iconClassName="w-5 h-5 text-light-900" />
              </Tooltip>
            )}
        </div>
      </div>

      {state?.options && state?.options.length > 0 && (
        <OList
          itemsClassname="text-sm pl-1 pt-1"
          items={state.options.map(({ label }) => label)}
        />
      )}
    </div>
  );
};

export default React.memo(TemplateQuestionPreview);
